import { Inject, Injectable, NgZone } from '@angular/core';
import { ReefLoginAccessTokenInput } from '@seco/core';
import { HttpClient, HttpHeaders, HttpContext, HttpParams } from '@angular/common/http';
import { Store, select } from '@ngrx/store';
import { Observable } from 'rxjs/internal/Observable';
import { of } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { JWT_TYPE_NAME, ReefHeaders, ReefTokenService } from '@seco/core';
import { ClpLoginResponse, LoginConfigService } from '@seco/login';
import {
  DirectUpdateClpConfig,
  ClpUpdateResponse,
  WebAppLoggedIn,
  WebappUpdateClpConfig,
  GetClpConfigError,
  DirectRefreshPage,
  SetSVCustomerConfig,
  SetSNOWCustomerConfig,
  SetAVCDashboardsCustomerConfig,
  SetAVCInvoicesCustomerConfig,
  SetAVCCreditLineStatusCustomerConfig,
  GetAVCCurrency,
  DirectLoggedIn,
  DirectJwtGenerated
} from './core.actions';
import * as AccountAction from '../../../../../../examples/direct-portal/src/app/redux/actions/loyalty/actions-account';
import { take, filter } from 'rxjs/operators';
import { getMenuListState, selectClpConfig, getDirectLoggedIn } from './login-index.reducer';
import { HttpService, ReefGatewayService } from '@seco/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { NgxPermissionsService } from 'ngx-permissions';
import { IdToken } from './../models';
import * as _ from 'lodash';
import { ConfigService } from '../helpers/config.service';
import { LanguageService } from './language.service';
import { LocaleMapperService } from '@uxdf/direct-shared-lib';
import { DOCUMENT } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { SvClpLoginResponse, TokenFragments } from './svClpLoginResponse';
import { localeMockResponse } from './locale.mock';
import {menuListResponse} from '../../../../../../examples/direct-portal/src/app/redux/services/utils/menu-list.mock';
import { BackendService } from '../../../../../../examples/direct-portal/src/app/redux/services/backend.service';

@Injectable()
export class LoginService {
  private readonly BUID_CONFIG_TOKEN = 'buildConfigToken';
  private readonly SIGN_IN_ACTION = 'UMSignInByAccessToken';
  private readonly LOG_IN_ACTION = 'authenticate';
  private helper = new JwtHelperService();
  menu;
  menuHtml;
  public unsignedAgreement = false;
  loggedInErrorMsg: string;

  private readonly url = {
    menu: storeCode => {
      return 'rest/' + storeCode + '/V1/amadeus/public/menu/list?scope[role]=3';
    },
    magento_login: storeCode => {
      return 'rest/' + storeCode + '/V1/amadeus/private/customer/login';
    },
    locale_list: storeCode => {
      return 'rest/' + storeCode + '/V1/amadeus/public/locale/list';
    }
  };

  constructor(
    private readonly store: Store<any>,
    private readonly httpService: HttpService,
    private readonly http: HttpClient,
    private readonly permissionsService: NgxPermissionsService,
    private readonly initService: ConfigService,
    private readonly loginConfigService: LoginConfigService,
    private readonly languageService: LanguageService,
    private readonly localeMapperService: LocaleMapperService,
    private readonly activatedRoute: ActivatedRoute,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly route: Router,
    private readonly gwService: ReefGatewayService,
    private readonly reeftokenservice: ReefTokenService,
    private readonly backendService: BackendService,
    private readonly ngZone: NgZone
  ) {
    this.gwService.setVersion('v3');
  }

  /**
   * Get token and nonce by calling AD service.
   */
  getClpConfig() {
    if (this.isAshRedirect) {
      this.dispatchLoginAction();
    }
    else {
      this.gwService.getAuthenticationToken().subscribe(
        (res: any) => {
          // Wrap in ngZone.run() so dispatchLoginAction() → loginConfigService.setConfig()
          // → @seco/login's viewReady=true all execute inside Angular's zone and
          // immediately trigger a change-detection cycle, showing the login form
          // on first load without requiring a user event (right-click).
          this.ngZone.run(() => {
            this.setLoginLocalStorage(res);
            this.dispatchLoginAction();
          });
        },
        () => {
         this.store.dispatch(new GetClpConfigError());
        }
      );
    }
  }

  /**
   * Login by access token
   */
  directLoginPipeline(SvClpLoginResponse: SvClpLoginResponse) {
    const token: IdToken | Promise<IdToken> = this.helper.decodeToken(SvClpLoginResponse.idToken);

    if (token instanceof Promise) {
      token.then(idToken => {
        this.proceedWithLogin(idToken, SvClpLoginResponse);
      });
    } else {
      this.proceedWithLogin(token, SvClpLoginResponse);
    }
  }

  proceedWithLogin(token: IdToken, SvClpLoginResponse: SvClpLoginResponse) {
    if (_.has(token, 'permissions_v2.A_DIRECT')) {
      this.permissionsService.loadPermissions(token.permissions_v2.A_DIRECT);
    }
    if (localStorage.getItem('source')?.toLocaleLowerCase() === 'ash' || localStorage.getItem('source')?.toLocaleLowerCase() === 'snow') {
      const req: any = {
        username: token.login,
        officeId: token.office,
        dutyCode: token.dutyCode
      };
      localStorage.setItem('login', JSON.stringify(req));
    }
    this.store.dispatch(
      new ClpUpdateResponse({
        accessToken: SvClpLoginResponse.accessToken,
        idToken: SvClpLoginResponse.idToken,
        nonce: SvClpLoginResponse.nonce,
        impersonatedCAN: SvClpLoginResponse.impersonatedCAN,
        impersonatedOID: SvClpLoginResponse.impersonatedOID,
        isSalesView: SvClpLoginResponse.isSalesView,
        impersonatedLSS: SvClpLoginResponse.impersonatedLSS,
        isASHView: SvClpLoginResponse.isASHView
      })
    );
    this.webAppLoginByAccessToken(SvClpLoginResponse);
  }

  webAppLoginByAccessToken(SvClpLoginResponse: SvClpLoginResponse) {
    if(this.isAshRedirect){
      this.gwService.getAuthenticationToken().subscribe(
        (res: any) => {
          this.setLoginLocalStorage(res);
          this.dispatchLoginAction();
          const decoded = this.helper.decodeToken(SvClpLoginResponse.idToken);
          if (decoded && decoded.nonce) {
                localStorage.setItem('nonce', decoded.nonce);
          }
          this.reefGatewayLogin(SvClpLoginResponse);
        }
      );
    }else{
      this.reefGatewayLogin(SvClpLoginResponse);
    }
  }

  reefGatewayLogin(SvClpLoginResponse: SvClpLoginResponse) {

    const loginInput: ReefLoginAccessTokenInput = {
      accessToken: SvClpLoginResponse.accessToken,
      idToken: SvClpLoginResponse.idToken,
      nonce: localStorage.getItem('nonce')
    };

    this.gwService.loginInByAccessToken(loginInput).subscribe((_res) => {
       if (!_res.JsessionId) {
            throw new Error(
              '[SUITS ERROR] GatewayService loginInByAccessToken should return a jwt to be used by the ReefServer module'
            );
          }
        this.store.dispatch(new WebAppLoggedIn({ token:_res.JsessionId }));
        const requestOptions = this.requestHeaders();
        const authToken = requestOptions.headers instanceof HttpHeaders 
          ? requestOptions.headers.get(ReefHeaders.AUTH) 
          : requestOptions.headers?.[ReefHeaders.AUTH];
        this.store.dispatch(new DirectJwtGenerated({
          token: authToken,
          code: 0
        }));
         this.store.dispatch(new AccountAction.GetAccount())
  });
}

requestHeaders(): {
    headers?: HttpHeaders | {
      [header: string]: string | string[];
    };
    context?: HttpContext;
    observe?: 'body';
    params?: HttpParams | {
      [param: string]: string | number | boolean;
    };
    reportProgress?: boolean;
    responseType?: 'json';
    withCredentials?: boolean;
  } {
    const options: any = { withCredentials: true };
    const jwtMap = this.reeftokenservice.getTokens();
    const jwt = jwtMap?.get(ReefHeaders.AUTH)?.get(JWT_TYPE_NAME);
      const headers = new HttpHeaders({
        Authorization: `${jwt}`
      });
      options.headers = headers;
    return options;
    
  }

  magentoLogin(token, store): Observable<any> {
    return this.http.get(localStorage.getItem('magento_url') + this.url.magento_login(store), {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  getLocaleList(storeCode): Observable<any> {
   return of(localeMockResponse); 
  }

  getMenuList(storeCode): Observable<any> {  
    return of(menuListResponse);
  }

  fetchTokensFromUrl(tokenFragments): TokenFragments {
    tokenFragments = tokenFragments.replace('?', '&');
    tokenFragments = tokenFragments.slice(1);
    const params = tokenFragments.split('&');
    let accessToken = '',
      idToken = '',
      language = '',
      sessionId = '',
      impersonatedCAN = '',
      impersonatedOID = '',
      isSalesView = '',
      impersonatedLSS = '';
    const isASHView = true;
    localStorage.setItem('isASHView', isASHView.toString());
    for (const param of params) {
      const queryParam = param.split('=')[0];
      const paramValue = param.split('=')[1];
      switch (queryParam) {
        case 'access_token': {
          accessToken = paramValue;
          break;
        }
        case 'id_token': {
          idToken = paramValue;
          break;
        }
        case 'storeLanguage': {
          language = paramValue;
          localStorage.setItem('language', language);
          break;
        }
        case 'centralSystemNumber': {
          impersonatedLSS = paramValue;
          localStorage.setItem('impersonatedLSS', impersonatedLSS);
          break;
        }
        case 'sessionId': {
          sessionId = paramValue;
          localStorage.setItem('SSOSessionId', sessionId);
          break;
        }
        case 'impersonatedCAN': {
          impersonatedCAN = paramValue;
          localStorage.setItem('impersonatedCAN', impersonatedCAN);
          break;
        }
        case 'impersonatedOID': {
          impersonatedOID = paramValue;
          localStorage.setItem('impersonatedOID', impersonatedOID);
          break;
        }
        case 'isSalesView': {
          if (paramValue === 'true') {
            this.store.dispatch(new SetSVCustomerConfig());
          }
          isSalesView = paramValue;
          localStorage.setItem('isSalesView', isSalesView);
          break;
        }
      }
    }
    return {
      accessToken,
      idToken,
      nonce: '',
      impersonatedCAN,
      impersonatedOID,
      isSalesView,
      language,
      impersonatedLSS,
      isASHView
    };
  }

  useSSODirectApiCall(SvClpLoginResponse) {
    if (SvClpLoginResponse && SvClpLoginResponse.accessToken) {
      localStorage.setItem('accessToken', SvClpLoginResponse.accessToken);
    }
    if (SvClpLoginResponse && SvClpLoginResponse.idToken) {
      localStorage.setItem('idToken', SvClpLoginResponse.idToken);
    }
    if (SvClpLoginResponse && SvClpLoginResponse.accessToken && SvClpLoginResponse.idToken) {
      this.directLoginPipeline(SvClpLoginResponse);
    } else {
      this.route.navigateByUrl('/unauthorized');
    }
  }

  setLanguageInSessionStorage(item) {
    // update session storage landing language
    this.localeMapperService.updateLandingAndCookie(item.locale, item.store_code);
    this.localeMapperService.updateSessionAndLanguageService(item.locale, item.store_code);
  }

  restoreToken(): Promise<any> {
    const token = localStorage.getItem('restoreToken');
    let ssoTokenFragments = null;
    const accessTokenCookie = localStorage.getItem('accessToken');
    const idToken = localStorage.getItem('idToken');

    let loginData = {
      accessToken: accessTokenCookie,
      idToken,
      nonce: ''
    };

    const parsedUrl = new URL(window.location.href);
    const params = new URLSearchParams(parsedUrl.search);
    const sourceServiceNow =  params.get('sourceServiceNow') === 'true';
    const sourceASH = parsedUrl.hash.includes('sourceASH') || params.get('sourceASH') === 'true';
 if (sourceASH || sourceServiceNow) {
      localStorage.setItem('source', sourceASH ? 'ash' : 'snow');
      if (!sourceASH) {
        this.store.dispatch(new SetSNOWCustomerConfig());
        const roles = (params.get('roles') ?? '').split(',').map(role => role.trim());
        if (roles.includes('A_DIRECT.VIEW_AVC_DASHBOARDS')) {
          this.store.dispatch(new SetAVCDashboardsCustomerConfig());
        }
        if (roles.includes('A_DIRECT.VIEW_AVC_INVOICES')) {
          this.store.dispatch(new SetAVCInvoicesCustomerConfig());
        }
        if (roles.includes('A_DIRECT.VIEW_AVC_CREDIT_LINE_STATUS')) {
          this.store.dispatch(new SetAVCCreditLineStatusCustomerConfig());
        }
        const currencyPrefix = 'MCONTRACT.AVC_TA_CURRENCY=';
        const currency = roles.find(role => role.startsWith(currencyPrefix))?.substring(currencyPrefix.length);
        if (currency && /^[A-Z]{3}$/.test(currency)) {
          this.store.dispatch(new GetAVCCurrency(currency));
        }
      }
      const ssoTokenFragments = this.fetchTokensFromUrl(window.location.hash);
      this.useSSODirectApiCall(ssoTokenFragments);
      // Wait for login to complete before allowing router to proceed
      return this.store.pipe(
        select(getDirectLoggedIn),
        filter(loggedIn => loggedIn === true),
        take(1)
      ).toPromise();
    } else {
      ssoTokenFragments = {
        ...loginData,
        impersonatedCAN: localStorage.getItem('impersonatedCAN'),
        impersonatedOID: localStorage.getItem('impersonatedOID'),
        isSalesView: localStorage.getItem('isSalesView'),
        impersonatedLSS: localStorage.getItem('impersonatedLSS'),
        isASHView: localStorage.getItem('isASHView')?.toLocaleLowerCase() === 'true',
        language: localStorage.getItem('language')
      };
      
      if(localStorage.getItem('isSalesView') === 'true'){
        this.store.dispatch(new SetSVCustomerConfig());
      }
    }

    const checkLoginSource = localStorage.getItem('source')?.toLowerCase() === 'ash' || localStorage.getItem('source')?.toLowerCase() === 'snow';

    if (!token || !this.sessionCanBeRestored) {
      return null;
    }

    return new Promise<void>(resolve => {
          this.backendService.initCustomerConfig().subscribe((data) => {
              this.store.dispatch(
            new DirectRefreshPage({
              token,
              code: 0,
              profile: data
            })
          );});

          //If we have an SSO page refresh(source is ASH), we use the SSO login data for subsequent calls
          // else we take the tokens required for regular AD refresh.
          loginData = checkLoginSource ? ssoTokenFragments : loginData;

          this.store.dispatch(new ClpUpdateResponse(loginData));
          this.webAppLoginByAccessToken(loginData);
          // return promise to get menu config before permission guards
          resolve(
            this.store
              .pipe(
                select(getMenuListState),
                filter(
                  menuState =>
                    false === menuState.isLoading && menuState.menuList && Object.keys(menuState.menuList).length > 0
                ),
                take(1)
              )
              .toPromise()
          );
        
    });
  }
  private sessionCanBeRestored(restoreToken: string) {
    return (
      localStorage.getItem('restoreToken') &&
      localStorage.getItem('accessToken') !== null &&
      localStorage.getItem('idToken') !== null &&
      !this.helper.isTokenExpired(restoreToken)
    );
  }
    private dispatchLoginAction() {
    this.store.dispatch(
      new DirectUpdateClpConfig({
        token: localStorage.getItem('configToken'),
        nonce: localStorage.getItem('nonce'),
        baseUrl: localStorage.getItem('baseUrl'),
        loginParameters: { officeNeeded: true }
      })
    );
    this.store.dispatch(
      new WebappUpdateClpConfig({
        token: localStorage.getItem('configToken'),
        nonce: localStorage.getItem('nonce'),
        baseUrl: localStorage.getItem('baseUrl'),
        loginParameters: { officeNeeded: true }
      })
    );
    this.loginConfigService.setConfig({
      clpAppId: 'MMM',
      clpConfig: this.store.select(selectClpConfig),
      language: this.languageService.getLanguageCLPCode(localStorage.getItem('landingLang')),
      useSSO: false,
      onSuccess: this.directLoginPipeline.bind(this)
    });
    localStorage.removeItem('configToken');
  }

  private setLoginLocalStorage(res: any) {
    localStorage.setItem('baseUrl', res.clpAuthURL);
    localStorage.setItem('nonce', res.nonce);
    localStorage.setItem('JsessionId', res.JsessionId);
    localStorage.setItem('configToken', res.configToken);
  }

  private get isAshRedirect(): boolean {
    return localStorage.getItem('isASHView')?.toLocaleLowerCase() === 'true';
  }
}
